import React, { useState, useEffect } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const StatCard = ({ title, value, subValue }) => (
  <div className="bg-white p-6 rounded-lg shadow">
    <h3 className="text-sm font-medium text-gray-500">{title}</h3>
    <p className="mt-2 text-3xl font-semibold text-gray-900">{value}</p>
    {subValue && <p className="text-sm text-gray-500">{subValue}</p>}
  </div>
);

const StatisticsPage = () => {
  const [prospects, setProspects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetch('https://venteclead.st-info.fr/get_stats_data.php');
        if (!response.ok) {
          throw new Error(`Erreur du serveur: ${response.status}`);
        }
        const data = await response.json();
        if (data.error) {
          throw new Error(data.error);
        }

        // Décoder l'historique JSON pour chaque prospect
        const decodedProspects = data.prospects.map(p => ({
          ...p,
          historique: p.historique ? JSON.parse(p.historique) : []
        }));

        setProspects(decodedProspects);
        setUsers(data.users || []);

      } catch (error) {
        console.error("Erreur lors de la récupération des données de statistiques:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return <div className="p-8 text-center">Chargement des statistiques...</div>;
  }
  
  // --- Calculs des statistiques ---

  // 1. Chiffres clés
  const totalProspects = prospects.length;
  const totalRdv = prospects.filter(p => p.etat_du_lead === 'Rendez-vous pris').length;
  const conversionRate = totalProspects > 0 ? ((totalRdv / totalProspects) * 100).toFixed(1) : 0;

  // 1.1. Délai de conversion moyen
  const conversionTimes = prospects
    .filter(p => p.historique && p.etat_du_lead === 'Rendez-vous pris' && p.created_at)
    .map(p => {
      const creationDate = new Date(p.created_at);
      const rdvEntry = p.historique.find(h => h.description && h.description.includes('Rendez-vous pris'));
      if (!rdvEntry) return null;
      const rdvDate = new Date(rdvEntry.date);
      return (rdvDate - creationDate) / (1000 * 60 * 60 * 24); // en jours
    })
    .filter(days => days !== null && days >= 0);
  
  const avgConversionTime = conversionTimes.length > 0
    ? (conversionTimes.reduce((a, b) => a + b, 0) / conversionTimes.length).toFixed(1)
    : 0;

  // 2. Répartition par état
  const statusCounts = prospects.reduce((acc, p) => {
    const etat = p.etat_du_lead || 'Non défini';
    acc[etat] = (acc[etat] || 0) + 1;
    return acc;
  }, {});
  const statusLabels = Object.keys(statusCounts);
  const statusData = {
    labels: statusLabels,
    datasets: [{
      label: 'Nombre de prospects',
      data: Object.values(statusCounts),
      backgroundColor: ['#FF6B6B80', '#4ECDC480', '#45B7D180', '#96CEB480', '#FFEEAD80', '#D4A5A580', '#9B5DE580', '#00BBF980', '#00F5D480', '#FEE44080', '#9B89B380', '#98DFEA80', '#F15BB580', '#FB560780', '#FF006E80'],
      borderColor: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD', '#D4A5A5', '#9B5DE5', '#00BBF9', '#00F5D4', '#FEE440', '#9B89B3', '#98DFEA', '#F15BB5', '#FB5607', '#FF006E'],
      borderWidth: 1,
    }],
  };

  // 3. Répartition par Fournisseur
  const sourceCounts = prospects.reduce((acc, p) => {
    const sourceName = p.source || 'Non défini';
    acc[sourceName] = (acc[sourceName] || 0) + 1;
    return acc;
  }, {});
  const sourceLabels = Object.keys(sourceCounts);
  const sourceData = {
    labels: sourceLabels,
    datasets: [{
      data: Object.values(sourceCounts),
      backgroundColor: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD', '#D4A5A5', '#9B5DE5', '#00BBF9', '#00F5D4', '#FEE440', '#9B89B3', '#98DFEA', '#F15BB5', '#FB5607', '#FF006E'],
    }],
  };
  
  // 3.1. Performance par Fournisseur
  const sourcePerformance = sourceLabels.map(sourceName => {
    const sourceProspects = prospects.filter(p => (p.source || 'Non défini') === sourceName);
    const total = sourceProspects.length;
    const conversions = sourceProspects.filter(p => p.etat_du_lead === 'Rendez-vous pris').length;
    const rate = total > 0 ? ((conversions / total) * 100).toFixed(1) : 0;
    return { sourceName, total, conversions, rate };
  });

  const supplierColors = sourceData.datasets[0].backgroundColor;

  const sourcePerformanceData = {
    labels: sourceLabels,
    datasets: [
      {
        label: 'Prospects Total',
        data: sourcePerformance.map(s => s.total),
        backgroundColor: supplierColors.map(color => `${color}80`), // 50% opacity
        borderColor: supplierColors,
        borderWidth: 1,
      },
      {
        label: 'RDV Pris',
        data: sourcePerformance.map(s => s.conversions),
        backgroundColor: supplierColors,
        borderColor: supplierColors,
        borderWidth: 1,
      }
    ]
  };

  // 4. Répartition par Département
  const departmentCounts = prospects.reduce((acc, p) => {
    if (p.code_postal) {
      const department = p.code_postal.toString().substring(0, 2);
      acc[department] = (acc[department] || 0) + 1;
    }
    return acc;
  }, {});
  const departmentLabels = Object.keys(departmentCounts).sort((a,b) => departmentCounts[b] - departmentCounts[a]).slice(0, 15);
  const departmentData = {
    labels: departmentLabels,
    datasets: [{
        label: 'Nombre de prospects',
      data: departmentLabels.map(label => departmentCounts[label]),
      backgroundColor: 'rgba(139, 92, 246, 0.5)',
      borderColor: 'rgba(139, 92, 246, 1)',
        borderWidth: 1,
    }],
  };

  // 5. Répartition par Projet
  const projectCounts = prospects.reduce((acc, p) => {
    const projectName = p.projet || 'Non défini';
    acc[projectName] = (acc[projectName] || 0) + 1;
    return acc;
  }, {});
  const projectLabels = Object.keys(projectCounts);
  const projectData = {
    labels: projectLabels,
    datasets: [{
        label: 'Nombre de prospects',
        data: Object.values(projectCounts),
        backgroundColor: supplierColors.map(color => `${color}80`), // 50% opacity
        borderColor: supplierColors,
        borderWidth: 1,
    }],
  };

  // 6. Performance par utilisateur
  const userPerformance = users.map(user => {
    const assignedProspects = prospects.filter(p => p.attribution === user);
    const assignedCount = assignedProspects.length;
    const salesCount = assignedProspects.filter(p => p.etat_du_lead === 'Rendez-vous pris').length;
    const userConversionRate = assignedCount > 0 ? ((salesCount / assignedCount) * 100).toFixed(1) : 0;
    return { name: user, assigned: assignedCount, sales: salesCount, rate: userConversionRate };
  });

  // 7. Activité dans le temps
  const activityByMonth = prospects.reduce((acc, p) => {
    if (p.created_at) {
      const month = new Date(p.created_at).toISOString().slice(0, 7);
      if (!acc[month]) acc[month] = { new: 0, rdv: 0 };
      acc[month].new += 1;
    }
    if (p.historique) {
      const rdvEntry = p.historique.find(h => h.description && h.description.includes('Rendez-vous pris'));
      if (rdvEntry) {
        const month = new Date(rdvEntry.date).toISOString().slice(0, 7);
        if (!acc[month]) acc[month] = { new: 0, rdv: 0 };
        acc[month].rdv += 1;
      }
    }
    return acc;
  }, {});
  const sortedMonths = Object.keys(activityByMonth).sort();
  const activityData = {
    labels: sortedMonths,
    datasets: [
      {
        label: 'Nouveaux prospects',
        data: sortedMonths.map(m => activityByMonth[m].new),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        yAxisID: 'y',
      },
      {
        label: 'RDV Pris',
        data: sortedMonths.map(m => activityByMonth[m].rdv),
        borderColor: 'rgb(20, 184, 166)',
        backgroundColor: 'rgba(20, 184, 166, 0.5)',
        yAxisID: 'y',
      },
    ],
  };

  // 8. Entonnoir de vente
  const funnelStages = ['Nouveau', 'À traiter', 'A rappeler', 'Rendez-vous pris'];
  const funnelStageOrder = { 'Nouveau': 0, 'À traiter': 1, 'A rappeler': 2, 'Rendez-vous pris': 3 };
  // S'assurer que les variantes sans accent sont aussi mappées
  const funnelStageOrderCompat = { ...funnelStageOrder, 'A traiter': 1 };
  const funnelCounts = { 'Nouveau': 0, 'À traiter': 0, 'A rappeler': 0, 'Rendez-vous pris': 0 };

  prospects.forEach(p => {
    // Utiliser un Set pour ne garder que les états uniques
    const prospectStates = new Set();
    
    // Ajouter l'état actuel
    if (p.etat_du_lead) {
      prospectStates.add(p.etat_du_lead);
    }

    // Ajouter les états de l'historique
    if (p.historique && Array.isArray(p.historique)) {
      p.historique.forEach(h => {
        if (h.description && h.description.includes('Rendez-vous pris')) prospectStates.add('Rendez-vous pris');
      });
    }

    // Trouver l'étape maximale atteinte par ce prospect
    let maxStageIndex = -1;
    prospectStates.forEach(state => {
      if (funnelStageOrderCompat.hasOwnProperty(state)) {
        const stageIndex = funnelStageOrderCompat[state];
        if (stageIndex > maxStageIndex) {
          maxStageIndex = stageIndex;
        }
      }
    });

    // Si le prospect a atteint au moins une étape de l'entonnoir,
    // l'ajouter à toutes les étapes jusqu'à l'étape maximale atteinte.
    if (maxStageIndex > -1) {
      for (let i = 0; i <= maxStageIndex; i++) {
        const stageName = funnelStages[i];
        funnelCounts[stageName]++;
      }
    }
  });

  const funnelData = {
    labels: funnelStages,
    datasets: [{
      label: 'Nombre de prospects',
      data: funnelStages.map(stage => funnelCounts[stage]),
      backgroundColor: [
        'rgba(54, 162, 235, 0.6)',
        'rgba(255, 206, 86, 0.6)',
        'rgba(255, 159, 64, 0.6)',
        'rgba(75, 192, 192, 0.6)',
      ],
      borderColor: [
        'rgba(54, 162, 235, 1)',
        'rgba(255, 206, 86, 1)',
        'rgba(255, 159, 64, 1)',
        'rgba(75, 192, 192, 1)',
      ],
      borderWidth: 1,
    }]
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Statistiques</h1>

      {/* Cartes des chiffres clés */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard title="Total Prospects" value={totalProspects} />
        <StatCard title="Rendez-vous Pris" value={totalRdv} />
        <StatCard title="Taux de Conversion" value={`${conversionRate}%`} />
        <StatCard title="Délai Conv. Moyen" value={`${avgConversionTime} jours`} />
      </div>
      
      {/* Activité et Entonnoir */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Activité par Mois</h2>
          <Line data={activityData} options={{ responsive: true, scales: { y: { beginAtZero: true } } }} />
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Entonnoir de Vente</h2>
          <Bar data={funnelData} options={{ responsive: true, indexAxis: 'y', plugins: { legend: { display: false } } }} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-8">
        {/* Graphiques */}
        <div className="lg:col-span-3 bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Répartition par État</h2>
          <Bar data={statusData} options={{ responsive: true, plugins: { legend: { display: false } } }} />
        </div>
        <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Répartition par Fournisseur</h2>
          <Doughnut data={sourceData} options={{ responsive: true }} />
        </div>
      </div>

      {/* Performance Fournisseur */}
      <div className="grid grid-cols-1 gap-8 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Performance par Fournisseur</h2>
            <Bar data={sourcePerformanceData} options={{ responsive: true }} />
        </div>
          </div>
      
      {/* Performance par Projet */}
      <div className="grid grid-cols-1 gap-8 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Répartition par Projet</h2>
              <Bar data={projectData} options={{ responsive: true, plugins: { legend: { display: false } } }} />
          </div>
      </div>
        
      {/* Tableau de performance */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Performance par Utilisateur</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateur</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prospects Assignés</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RDV Pris</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Taux de Conversion</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {userPerformance.map((user, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.assigned}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.sales}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StatisticsPage; 